// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Modelindstillinger that outlive the Ground (frontend-38f). Three layers,
 * merged over `DEFAULT_SETTINGS` in this order:
 *
 *  1. **Global preferences** (`localStorage['rux.viewer.<user>.prefs']`): how
 *     the user likes to look at any model — Rum-etiketter, point size,
 *     ghosting, wireframe, projection, light.
 *  2. **Per case** (`localStorage['rux.viewer.<user>.case.<cid>']`): what this
 *     building is shown as — Farver (RGB, lens, label) and its label cloud,
 *     which clouds, mesh and model layers are on, Tæthed, Klip, the locked
 *     plan, Vis omgivelser.
 *  3. **This page load** (in memory, per case): everything above plus the
 *     clipping box and its tool — scene-local values that only mean
 *     something next to the scene they were drawn in (the Ground keeps its
 *     recentring origin across a remount, `viewerCache.ts`), so a round trip
 *     through an expert page keeps them but a reload does not.
 *
 * Keys are scoped by the signed-in user (`AuthUser.id`; the server is the
 * page's own origin — the API is same-origin `/api/v1` — which
 * `localStorage` already partitions by). Signing out clears every
 * `rux.viewer.*` key (`clearViewerStorage`), and the case list drops the
 * per-case keys of cases that no longer exist (`pruneCaseSettings`): case
 * ids are slugs of the file name and are reused when a deleted case's name
 * comes back.
 *
 * Never kept: one-shot requests (`frameNonce`, `focusBox`, `view`) and Vælg punkt.
 *
 * Stored values are versioned (`{ v, s }`) and hold only what differs from
 * the defaults; every field is validated on the way in, so a value from an
 * older or newer build, a hand edit or a renamed option falls back to its
 * default field by field rather than taking the whole panel down. Every storage access may throw (private mode, blocked
 * storage, quota) and is caught: the panel then simply starts at defaults.
 *
 * Pure: the storage is passed in.
 */

import { DEFAULT_LIGHTING, type LightingState } from '../../../viewport/cameraViews';
import type { SerializedElementFilter } from '../../../viewport/elementFilter';
import { DEFAULT_SETTINGS, GHOST_TRANSPARENCY, METRE_SIZE, type ModelSettingsState } from './settingsState';

/** Schema version of both stored blobs. Bump it with a migration in `MIGRATIONS`. */
export const SETTINGS_VERSION = 1;

/** Every key this module writes starts with this. */
export const VIEWER_KEY_PREFIX = 'rux.viewer.';

/** The user scope of the keys: the user id, or `anon` before one is known. */
function userPart(user: string | null | undefined): string {
  return user ? encodeURIComponent(user) : 'anon';
}

export function prefsKey(user: string | null | undefined): string {
  return `${VIEWER_KEY_PREFIX}${userPart(user)}.prefs`;
}

function casePrefix(user: string | null | undefined): string {
  return `${VIEWER_KEY_PREFIX}${userPart(user)}.case.`;
}

export function caseKey(user: string | null | undefined, cid: string): string {
  return `${casePrefix(user)}${cid}`;
}

/**
 * The Ground's measurements (Mål afstand / Mål areal) for `user` in case
 * `cid`, kept in `sessionStorage` for the tab's session. Under the same
 * prefix, so signing out clears them (`clearViewerStorage`).
 */
export function measureKey(user: string | null | undefined, cid: string): string {
  return `${VIEWER_KEY_PREFIX}${userPart(user)}.measure.${cid}`;
}

/**
 * Rediger › Geometri's guides and dimension chains (frontend-u0o.11) for
 * `user` in case `cid`, kept in `localStorage` until the server can store
 * them (ReUseX reusex-8cug). Under the viewer prefix, so signing out
 * clears them with the rest.
 */
export function annotationsKey(user: string | null | undefined, cid: string): string {
  return `${VIEWER_KEY_PREFIX}${userPart(user)}.cad.${cid}`;
}

export type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;
/** What the clean-ups need besides: walking and removing keys. */
export type SweepableStorage = Pick<Storage, 'length' | 'key' | 'removeItem'>;

/** The global preferences. */
export const PREF_FIELDS = [
  'roomLabels',
  'size',
  'sizeMetres',
  'ghost',
  'ghostTransparency',
  'wireframe',
  'projection',
  'lighting',
] as const satisfies readonly (keyof ModelSettingsState)[];

/** Kept per case in `localStorage`. */
export const CASE_FIELDS = [
  'colour',
  'labelSource',
  'clouds',
  'mesh',
  'modelLayers',
  'panoramas',
  'posegraph',
  'render',
  'density',
  'clipOn',
  'planLock',
  'showOutside',
  'buildingElements',
] as const satisfies readonly (keyof ModelSettingsState)[];

/** Kept per case for this page load only (scene-local). */
export const SESSION_FIELDS = ['clip', 'clipTool'] as const satisfies readonly (keyof ModelSettingsState)[];

type Field = keyof ModelSettingsState;

// ------------------------------------------------------------ validation ----

const isBool = (v: unknown): v is boolean => typeof v === 'boolean';
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const oneOf =
  <T extends string | number>(...values: T[]) =>
  (v: unknown): v is T =>
    values.includes(v as T);

function isBoolRecord(v: unknown): v is Record<string, boolean> {
  return typeof v === 'object' && v !== null && !Array.isArray(v) && Object.values(v).every(isBool);
}

function isCorner(v: unknown): boolean {
  if (typeof v !== 'object' || v === null) return false;
  const c = v as Record<string, unknown>;
  return isNum(c.x) && isNum(c.y) && isNum(c.z);
}

function isLighting(v: unknown): v is LightingState {
  if (typeof v !== 'object' || v === null) return false;
  const l = v as Record<string, unknown>;
  return (Object.keys(DEFAULT_LIGHTING) as (keyof LightingState)[]).every((k) => isNum(l[k]));
}

function isElementFilter(v: unknown): v is SerializedElementFilter {
  if (typeof v !== 'object' || v === null) return false;
  const f = v as Record<string, unknown>;
  if (!Array.isArray(f.hidden) || !f.hidden.every((h) => typeof h === 'string')) return false;
  if (typeof f.storey !== 'object' || f.storey === null) return false;
  const s = f.storey as Record<string, unknown>;
  return (s.storeyId === null || isNum(s.storeyId)) && isBool(s.andBelow);
}

/** Whether `v` is a valid value of `field`. */
const VALID: Record<Field, (v: unknown) => boolean> = {
  panoramas: isBool,
  posegraph: isBool,
  roomLabels: isBool,
  mesh: isBool,
  modelLayers: isBoolRecord,
  planLock: isBool,
  render: oneOf('points', 'splat'),
  density: oneOf(25, 50, 100),
  size: oneOf('S', 'M', 'L'),
  ghost: oneOf('svag', 'normal', 'staerk'),
  ghostTransparency: (v) => isNum(v) && v >= GHOST_TRANSPARENCY.min && v <= GHOST_TRANSPARENCY.max,
  clip: (v) => v === null || (typeof v === 'object' && isCorner((v as { min?: unknown }).min) && isCorner((v as { max?: unknown }).max)),
  clipTool: isBool,
  clipOn: isBool,
  frameNonce: () => false,
  focusBox: () => false,
  clouds: isBoolRecord,
  colour: oneOf('lens', 'rgb', 'label'),
  labelSource: (v) => v === null || typeof v === 'string',
  sizeMetres: (v) => v === null || (isNum(v) && v >= METRE_SIZE.min && v <= METRE_SIZE.max),
  wireframe: isBool,
  projection: oneOf('perspective', 'orthographic'),
  view: () => false,
  lighting: isLighting,
  picking: () => false,
  showOutside: isBool,
  buildingElements: isElementFilter,
};

/** The valid `fields` of `source`, as a patch. */
function pick(source: unknown, fields: readonly Field[]): Partial<ModelSettingsState> {
  const out: Record<string, unknown> = {};
  if (typeof source !== 'object' || source === null) return out;
  const s = source as Record<string, unknown>;
  for (const f of fields) if (f in s && VALID[f](s[f])) out[f] = s[f];
  return out as Partial<ModelSettingsState>;
}

// ------------------------------------------------------------ versioning ----

/**
 * `v → v + 1` upgrades of a stored settings object. None yet: version 1 is
 * the first. A migration renames or reshapes fields; anything it leaves
 * invalid still falls back to its default in `pick`.
 */
const MIGRATIONS: Record<number, (s: Record<string, unknown>) => Record<string, unknown>> = {};

/** The settings object a stored blob carries, upgraded to `SETTINGS_VERSION`; null when unreadable or from a newer build. */
export function parseStored(raw: string | null, migrations = MIGRATIONS, version = SETTINGS_VERSION): Record<string, unknown> | null {
  if (raw === null) return null;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const { v, s } = value as { v?: unknown; s?: unknown };
  if (typeof v !== 'number' || !Number.isInteger(v) || v < 1 || v > version) return null;
  if (typeof s !== 'object' || s === null || Array.isArray(s)) return null;
  let settings = s as Record<string, unknown>;
  for (let from = v; from < version; from += 1) {
    const step = migrations[from];
    if (!step) return null;
    settings = step(settings);
  }
  return settings;
}

/**
 * The blob for `fields` of `state`: only the fields that differ from the
 * defaults, so a choice nobody made follows the defaults of later builds
 * (Rum-etiketter turning off by default reaches everyone who never touched it).
 */
function serialise(state: ModelSettingsState, fields: readonly Field[]): string {
  const s: Record<string, unknown> = {};
  for (const f of fields) {
    if (JSON.stringify(state[f]) !== JSON.stringify(DEFAULT_SETTINGS[f])) s[f] = state[f];
  }
  return JSON.stringify({ v: SETTINGS_VERSION, s });
}

function read(storage: StorageLike | null, key: string): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

// --------------------------------------------------------- the session ----

/** This page load's settings per user and case (layer 3). */
const session = new Map<string, ModelSettingsState>();
const sessionKey = (user: string | null | undefined, cid: string) => `${userPart(user)}\u0000${cid}`;

/** Forget this page load's settings (tests). */
export function clearSessionSettings(): void {
  session.clear();
}

// ------------------------------------------------------------ load/save ----

/**
 * The settings the Ground starts with for `user` in case `cid`: defaults,
 * then the global preferences, then the case's, then this page load's.
 * `cid` undefined (no case, a test) is the defaults plus the preferences.
 */
export function loadSettings(storage: StorageLike | null, user: string | null | undefined, cid: string | undefined): ModelSettingsState {
  const prefs = pick(parseStored(read(storage, prefsKey(user))), PREF_FIELDS);
  const perCase = cid === undefined ? {} : pick(parseStored(read(storage, caseKey(user, cid))), CASE_FIELDS);
  const kept = cid === undefined ? undefined : session.get(sessionKey(user, cid));
  const sessionPatch = kept ? pick(kept, [...PREF_FIELDS, ...CASE_FIELDS, ...SESSION_FIELDS]) : {};
  return { ...DEFAULT_SETTINGS, ...prefs, ...perCase, ...sessionPatch };
}

/** What `saveSettings` last wrote per key, so an unchanged blob is not rewritten on every dispatch. */
export type WrittenCache = Map<string, string>;

/**
 * Keeps `state` for case `cid`: this page load's copy always, and the
 * stored blobs when they changed. Storage errors are swallowed (the panel
 * keeps working, it just will not remember).
 */
export function saveSettings(
  storage: StorageLike | null,
  user: string | null | undefined,
  cid: string | undefined,
  state: ModelSettingsState,
  written: WrittenCache,
): void {
  if (cid !== undefined) session.set(sessionKey(user, cid), state);
  if (!storage) return;
  const blobs: [string, string][] = [[prefsKey(user), serialise(state, PREF_FIELDS)]];
  if (cid !== undefined) blobs.push([caseKey(user, cid), serialise(state, CASE_FIELDS)]);
  for (const [key, blob] of blobs) {
    if (written.get(key) === blob) continue;
    try {
      storage.setItem(key, blob);
      written.set(key, blob);
    } catch {
      // Quota, private mode, blocked storage: nothing to do but not remember.
    }
  }
}

/** Every key in `storage` starting with `prefix`; [] when walking it throws. */
function keysWith(storage: SweepableStorage, prefix: string): string[] {
  const out: string[] = [];
  try {
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key !== null && key.startsWith(prefix)) out.push(key);
    }
  } catch {
    return [];
  }
  return out;
}

function removeAll(storage: SweepableStorage, keys: readonly string[]): number {
  let n = 0;
  for (const key of keys) {
    try {
      storage.removeItem(key);
      n += 1;
    } catch {
      // Blocked storage: nothing to remove then either.
    }
  }
  return n;
}

/**
 * Signing out: forgets every viewer setting in this browser, every user's
 * (and this page load's), and the tab's measurements (`sessionStore`).
 */
export function clearViewerStorage(storage: SweepableStorage | null, sessionStore: SweepableStorage | null = null): number {
  session.clear();
  const local = storage ? removeAll(storage, keysWith(storage, VIEWER_KEY_PREFIX)) : 0;
  return local + (sessionStore ? removeAll(sessionStore, keysWith(sessionStore, VIEWER_KEY_PREFIX)) : 0);
}

/**
 * The case list loaded: drops `user`'s per-case settings for cases not in
 * `liveCids` (deleted ones — a case id is a name slug and may come back as
 * a different case). Other users' keys are left alone: their lists are not
 * this one.
 */
export function pruneCaseSettings(storage: SweepableStorage | null, user: string | null | undefined, liveCids: Iterable<string>): number {
  if (!storage) return 0;
  const live = new Set(liveCids);
  const prefix = casePrefix(user);
  return removeAll(
    storage,
    keysWith(storage, prefix).filter((k) => !live.has(k.slice(prefix.length))),
  );
}

/** This tab's `sessionStorage`, or null where touching it throws. */
export function browserSessionStorage(): (StorageLike & SweepableStorage & Pick<Storage, 'removeItem'>) | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** This browser's `localStorage`, or null where touching it throws. */
export function browserStorage(): (StorageLike & SweepableStorage) | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
